import { useMemo } from 'react';
import { CalendarEvent as SchemaCalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { Operatory } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/operatory.pb';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import type {
  CalendarColumnGroup,
  CalendarColumnHeaderType,
  OperatoryCalendarColumn,
  ProviderCalendarColumn,
} from '@frontend/calendar-grid';
import { IconName } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { ScheduleRequestEvent } from '@frontend/schedule-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { usePreference } from '@frontend/user-preferences';
import {
  getOverlappingEventIndicatorVariant,
  OverlappingEventIndicatorVariantType,
} from '../../components/calendar-view/calendar-view-v3/overlapping-event-indicator';
import { GridCalendarEvent } from '../../components/calendar-view/events-cards/grid-event-card-switch';
import { useCalendarLocationIds } from '../../stores/calendar-location.store';
import { useCalendarStore } from '../../stores/calendar.store';
import { getLocalTimezone } from '../../utils/date-helpers';
import { CalendarEntitySchedule } from './calendar-schedule-definitions';

dayjs.extend(customParseFormat);

export type CalendarGridV3Props = {
  calendarEventsWithConvertedTimes: SchemaCalendarEvent[];
  scheduledRequestsWithConvertedTimes: ScheduleRequestEvent[];
  sortedProviders: (Provider & { fullName: string })[];
  sortedOperatories: Operatory[];
  providerSchedules: CalendarEntitySchedule[];
};

type CalendarGridV3DataByType<THeader extends CalendarColumnHeaderType> = {
  headerType: THeader;
  columns: THeader extends 'provider' ? ProviderCalendarColumn[] : OperatoryCalendarColumn[];
  events: GridCalendarEvent[];
  columnGroups: CalendarColumnGroup[];
};

type CalendarGridV3Data = CalendarGridV3DataByType<'provider'> | CalendarGridV3DataByType<'operatory'>;

export const useCalendarGridV3Data = ({
  calendarEventsWithConvertedTimes,
  scheduledRequestsWithConvertedTimes,
  sortedProviders,
  sortedOperatories,
  providerSchedules,
}: CalendarGridV3Props): CalendarGridV3Data => {
  const { calendarEntityType, calendarDate } = useCalendarStore(['calendarEntityType', 'calendarDate']);
  const { calendarLocationIds } = useCalendarLocationIds();
  const { getScopeName } = useAppScopeStore();
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const [columnEntityOrder] = usePreference('ScheduleCalendarViewColumnOrder');

  const isOperatoryView = calendarEntityType === 'operatory';
  const headerType: CalendarColumnHeaderType = isOperatoryView ? 'operatory' : 'provider';

  return useMemo((): CalendarGridV3Data => {
    const events: GridCalendarEvent[] = [];
    const columnGroups: CalendarColumnGroup[] = [];

    let groupOffset = 0;

    if (isOperatoryView) {
      const columns: OperatoryCalendarColumn[] = [];

      for (const locationId of calendarLocationIds) {
        const columnIds: string[] = [];

        // Operatory view
        const locationOperatories = sortedOperatories.filter((o) => o.locationId === locationId);

        locationOperatories.forEach((operatory) => {
          const operatoryId = operatory.id ?? '';
          if (!operatoryId) return;
          const operatoryName = operatory.displayName || operatory.externalName || '';
          const eventIds: string[] = [];
          let operatoryAppointmentsCount = 0;

          // Operatory appointments
          const operatoryEvents = calendarEventsWithConvertedTimes.filter(
            (event) => event.locationId === locationId && event.operatoryId === operatoryId
          );
          for (const event of operatoryEvents) {
            if (!event.id || !event.startTime) continue;
            const { startTime, duration, isYesterdayEvent, overlappingIndicatorVariant } = getGridEventTiming(
              calendarDate,
              event.startDate,
              event.startTime,
              event.endDate,
              event.endTime
            );

            // ref: getMappedCalendarEventData of libs/features/schedule/src/hooks/scheduler-v3/use-get-calendar-events-v3.ts
            events.push({
              id: event.id,
              startTime,
              duration,
              title: getName(event),
              isYesterdayEvent,
              overlappingIndicatorVariant,
              continuesFromPreviousDay: isYesterdayEvent,
              continuesToNextDay: !isYesterdayEvent && !!overlappingIndicatorVariant,
              eventType: 'appointment',
              eventStatus: event.attendeeStatus,
              eventTypeColor: event.displayOptions?.primaryColor,
              backgroundColor: event.displayOptions?.backgroundColor,
              displayOptions: {
                primaryColor: event.displayOptions?.primaryColor,
                backgroundColor: event.displayOptions?.backgroundColor,
                textColor: event.displayOptions?.textColor,
              },
              // Drag-drop enriched fields
              startHour: dayjs(event.startTime, 'HH:mm:ss').format('hh:mm A'),
              endHour: dayjs(event.endTime, 'HH:mm:ss').format('hh:mm A'),
              startDate: event.startDate,
              endDate: event.endDate,
              locationId: event.locationId,
              locationName: getScopeName(event.locationId ?? ''),
              entityId: operatoryId,
              entityName: operatoryName,
              // Operatory schema does not expose a calendarId; leave undefined for operatory view
              patientId: event.attendeeId,
              referenceId: event.referenceId,
              appointmentTypeId: event.referenceTypeId,
              appointmentTypeName: event.details?.rTyNa,
            });
            eventIds.push(event.id);
            operatoryAppointmentsCount++;
          }

          // Hide operatory if it is hidden
          if (operatory.hidden) return;

          // operatory working hours, breaks & exceptions
          const operatorySchedule = providerSchedules.find((s) => s.id === operatoryId && s.locationId === locationId);

          (operatorySchedule?.resolvedWorkingHours ?? []).forEach((wh) => {
            const startTime = dayjs(wh.startTime, 'hh:mm A').format('HH:mm');
            const id = `working-hours-${operatoryId}-${wh.startTime}-${wh.duration}`;

            events.push({
              id,
              startTime,
              duration: wh.duration,
              eventType: 'workingHoursV3',
            });
            eventIds.push(id);

            (wh.breaks ?? []).forEach((b) => {
              const breakStartTime = dayjs(b.startTime, 'hh:mm A').format('HH:mm');
              const breakId = `working-hours-break-${operatoryId}-${b.startTime}-${b.duration}`;

              events.push({
                id: breakId,
                startTime: breakStartTime,
                duration: b.duration,
                eventType: 'nonWorkingHoursV3',
              });
              eventIds.push(breakId);
            });
          });

          (operatorySchedule?.resolvedExceptions ?? []).forEach((ex) => {
            const startTime = dayjs(ex.startTime, 'hh:mm A').format('HH:mm');
            const id = `working-hours-${operatoryId}-${ex.startTime}-${ex.duration}`;

            events.push({
              id,
              startTime,
              duration: ex.duration,
              eventType: 'nonWorkingHoursV3',
            });
            eventIds.push(id);
          });

          // Operatory schedule requests
          const operatoryRequests = scheduledRequestsWithConvertedTimes.filter(
            (sr) => sr.locationId === locationId && sr.operatoryId === operatoryId
          );
          // ref: getMappedScheduleRequestData in libs/features/schedule/src/hooks/scheduler-v3/use-get-calendar-events-v3.ts
          for (const sr of operatoryRequests) {
            if (!sr.request.id || !sr.startTime) continue;
            const eventId = sr.request.id;
            const { startTime, duration, isYesterdayEvent, overlappingIndicatorVariant } = getGridEventTiming(
              calendarDate,
              sr.startDate,
              sr.startTime,
              sr.endDate,
              sr.endTime
            );
            events.push({
              id: eventId,
              startTime,
              duration,
              isYesterdayEvent,
              overlappingIndicatorVariant,
              continuesFromPreviousDay: isYesterdayEvent,
              continuesToNextDay: !isYesterdayEvent && !!overlappingIndicatorVariant,
              eventType: 'scheduleRequest',
              // Enriched fields for GridScheduleRequestCard
              scheduleRequestEventData: sr.request,
              entityId: operatoryId,
              entityName: operatoryName,
              locationId: sr.locationId,
              locationName: getScopeName(sr.locationId ?? ''),
              startDate: sr.startDate,
              startHour: dayjs(sr.startTime, 'HH:mm:ss').format('hh:mm A'),
              endHour: dayjs(sr.endTime, 'HH:mm:ss').format('hh:mm A'),
              eventTypeColor: sr.displayOptions.primaryColor,
              backgroundColor: sr.displayOptions.backgroundColor,
              calendarDate: calendarDate,
              appointmentTypeName: sr.appointmentTypeName,
            } as GridCalendarEvent);
            eventIds.push(eventId);
          }

          columns.push({
            id: operatoryId,
            name: operatoryName,
            operatoryIcon: (operatory.details?.icon?.name as IconName) || 'chair',
            operatoryIconColor: operatory.details?.icon?.color,
            eventIds: eventIds,
            count: operatoryAppointmentsCount,
          } as OperatoryCalendarColumn);
          columnIds.push(operatoryId);
        });

        // Unassigned operatory events
        const unassignedEvents = calendarEventsWithConvertedTimes.filter(
          (event) => event.locationId === locationId && !event.operatoryId
        );
        if (unassignedEvents.length > 0) {
          const unassignedColumnId = `unassigned-operatory-${locationId}`;
          const eventIds: string[] = [];
          for (const event of unassignedEvents) {
            if (!event.id || !event.startTime) continue;
            const { startTime, duration, isYesterdayEvent, overlappingIndicatorVariant } = getGridEventTiming(
              calendarDate,
              event.startDate,
              event.startTime,
              event.endDate,
              event.endTime
            );
            events.push({
              id: event.id,
              title: getName(event),
              startTime,
              duration,
              isYesterdayEvent,
              overlappingIndicatorVariant,
              continuesFromPreviousDay: isYesterdayEvent,
              continuesToNextDay: !isYesterdayEvent && !!overlappingIndicatorVariant,
              eventType: 'appointment',
              eventStatus: event.attendeeStatus,
              eventTypeColor: event.displayOptions?.primaryColor,
              backgroundColor: event.displayOptions?.backgroundColor,
              displayOptions: {
                primaryColor: event.displayOptions?.primaryColor,
                backgroundColor: event.displayOptions?.backgroundColor,
                textColor: event.displayOptions?.textColor,
              },
              // Drag-drop enriched fields
              startHour: dayjs(event.startTime, 'HH:mm:ss').format('hh:mm A'),
              endHour: dayjs(event.endTime, 'HH:mm:ss').format('hh:mm A'),
              startDate: event.startDate,
              endDate: event.endDate,
              locationId: event.locationId,
              locationName: getScopeName(event.locationId ?? ''),
              patientId: event.attendeeId,
              referenceId: event.referenceId,
              appointmentTypeId: event.referenceTypeId,
              appointmentTypeName: event.details?.rTyNa,
            });
            eventIds.push(event.id);
          }
          columns.push({
            id: unassignedColumnId,
            name: 'Unassigned',
            operatoryIcon: 'chair' as IconName,
            eventIds: eventIds,
            count: eventIds.length,
          } as OperatoryCalendarColumn);
          columnIds.push(unassignedColumnId);
        }

        columnGroups.push({
          id: locationId,
          name: calendarLocationIds.length > 1 ? getScopeName(locationId) : '',
          columnIds: applyColumnOrder(columnIds, columnEntityOrder?.operatory?.[locationId]),
          groupOffset,
          timezone: locationTimezoneMap[locationId]?.timezone || getLocalTimezone(),
        });

        groupOffset += columnIds.length;
      }

      return { headerType, columns, events, columnGroups } as CalendarGridV3DataByType<'operatory'>;
    } else {
      const columns: ProviderCalendarColumn[] = [];

      for (const locationId of calendarLocationIds) {
        const columnIds: string[] = [];

        // Provider view
        const locationProviders = sortedProviders.filter((p) => p.locationId === locationId);

        locationProviders.forEach((provider) => {
          const providerId = provider.id ?? '';
          if (!providerId) return;
          const providerName = getFullName(provider);
          const eventIds: string[] = [];
          let providerAppointmentsCount = 0;

          // Provider appointments
          const providerEvents = calendarEventsWithConvertedTimes.filter(
            (event) => event.locationId === locationId && event.organizerId === providerId
          );
          for (const event of providerEvents) {
            if (!event.id || !event.startTime) continue;
            const { startTime, duration, isYesterdayEvent, overlappingIndicatorVariant } = getGridEventTiming(
              calendarDate,
              event.startDate,
              event.startTime,
              event.endDate,
              event.endTime
            );

            // ref: getMappedCalendarEventData of libs/features/schedule/src/hooks/scheduler-v3/use-get-calendar-events-v3.ts
            events.push({
              id: event.id,
              startTime,
              duration,
              title: getName(event),
              isYesterdayEvent,
              overlappingIndicatorVariant,
              continuesFromPreviousDay: isYesterdayEvent,
              continuesToNextDay: !isYesterdayEvent && !!overlappingIndicatorVariant,
              eventType: 'appointment',
              eventStatus: event.attendeeStatus,
              eventTypeColor: event.displayOptions?.primaryColor,
              backgroundColor: event.displayOptions?.backgroundColor,
              displayOptions: {
                primaryColor: event.displayOptions?.primaryColor,
                backgroundColor: event.displayOptions?.backgroundColor,
                textColor: event.displayOptions?.textColor,
              },
              // Drag-drop enriched fields
              startHour: dayjs(event.startTime, 'HH:mm:ss').format('hh:mm A'),
              endHour: dayjs(event.endTime, 'HH:mm:ss').format('hh:mm A'),
              startDate: event.startDate,
              endDate: event.endDate,
              locationId: event.locationId,
              locationName: getScopeName(event.locationId ?? ''),
              entityId: providerId,
              entityName: providerName,
              calendarId: provider.calendarId,
              patientId: event.attendeeId,
              referenceId: event.referenceId,
              appointmentTypeId: event.referenceTypeId,
              appointmentTypeName: event.details?.rTyNa,
            });
            eventIds.push(event.id);
            providerAppointmentsCount++;
          }

          // If provider is archived, show them only if they have events; otherwise show them if they are not hidden
          const isProviderVisible = provider.isArchived ? !!eventIds.length : !provider.hidden;
          if (!isProviderVisible) return;

          // provider working hours, breaks & exceptions
          const providerSchedule = providerSchedules.find((s) => s.id === providerId && s.locationId === locationId);

          (providerSchedule?.resolvedWorkingHours ?? []).forEach((wh) => {
            const startTime = dayjs(wh.startTime, 'hh:mm A').format('HH:mm');
            const id = `working-hours-${providerId}-${wh.startTime}-${wh.duration}`;

            events.push({
              id,
              startTime,
              duration: wh.duration,
              eventType: 'workingHoursV3',
            });
            eventIds.push(id);

            (wh.breaks ?? []).forEach((b) => {
              const breakStartTime = dayjs(b.startTime, 'hh:mm A').format('HH:mm');
              const breakId = `working-hours-break-${providerId}-${b.startTime}-${b.duration}`;

              events.push({
                id: breakId,
                startTime: breakStartTime,
                duration: b.duration,
                eventType: 'nonWorkingHoursV3',
              });
              eventIds.push(breakId);
            });
          });

          (providerSchedule?.resolvedExceptions ?? []).forEach((ex) => {
            const startTime = dayjs(ex.startTime, 'hh:mm A').format('HH:mm');
            const id = `working-hours-${providerId}-${ex.startTime}-${ex.duration}`;

            events.push({
              id,
              startTime,
              duration: ex.duration,
              eventType: 'nonWorkingHoursV3',
            });
            eventIds.push(id);
          });

          // Provider schedule requests
          const providerRequests = scheduledRequestsWithConvertedTimes.filter(
            (sr) => sr.locationId === locationId && (sr.organizerId === providerId || sr.providerName === providerName)
          );
          // ref: getMappedScheduleRequestData in libs/features/schedule/src/hooks/scheduler-v3/use-get-calendar-events-v3.ts
          providerRequests.forEach((sr) => {
            if (!sr.request.id || !sr.startTime) return;
            const { startTime, duration, isYesterdayEvent, overlappingIndicatorVariant } = getGridEventTiming(
              calendarDate,
              sr.startDate,
              sr.startTime,
              sr.endDate,
              sr.endTime
            );
            events.push({
              id: sr.request.id,
              startTime,
              duration,
              isYesterdayEvent,
              overlappingIndicatorVariant,
              continuesFromPreviousDay: isYesterdayEvent,
              continuesToNextDay: !isYesterdayEvent && !!overlappingIndicatorVariant,
              eventType: 'scheduleRequest',
              // Enriched fields for GridScheduleRequestCard
              scheduleRequestEventData: sr.request,
              entityId: providerId,
              entityName: providerName,
              locationId: sr.locationId,
              locationName: getScopeName(sr.locationId ?? ''),
              startDate: sr.startDate,
              startHour: dayjs(sr.startTime, 'HH:mm:ss').format('hh:mm A'),
              endHour: dayjs(sr.endTime, 'HH:mm:ss').format('hh:mm A'),
              eventTypeColor: sr.displayOptions.primaryColor,
              backgroundColor: sr.displayOptions.backgroundColor,
              calendarDate: calendarDate,
              appointmentTypeName: sr.appointmentTypeName,
            } as GridCalendarEvent);
            eventIds.push(sr.request.id);
          });

          columns.push({
            id: providerId,
            name: getFullName(provider),
            providerImage: provider.profile?.picUrl ?? '',
            eventIds: eventIds,
            type: provider.isArchived ? 'archived' : undefined,
            count: providerAppointmentsCount,
          } as ProviderCalendarColumn);
          columnIds.push(providerId);
        });

        // Unassigned provider events
        const unassignedEvents = calendarEventsWithConvertedTimes.filter(
          (e) => e.locationId === locationId && !e.organizerId
        );
        if (unassignedEvents.length > 0) {
          const unassignedColumnId = `unassigned-provider-${locationId}`;
          const eventIds: string[] = [];
          unassignedEvents.forEach((event) => {
            if (!event.id || !event.startTime) return;

            const { startTime, duration, isYesterdayEvent, overlappingIndicatorVariant } = getGridEventTiming(
              calendarDate,
              event.startDate,
              event.startTime,
              event.endDate,
              event.endTime
            );

            events.push({
              id: event.id,
              title: getName(event),
              startTime,
              duration,
              isYesterdayEvent,
              overlappingIndicatorVariant,
              continuesFromPreviousDay: isYesterdayEvent,
              continuesToNextDay: !isYesterdayEvent && !!overlappingIndicatorVariant,
              eventType: 'appointment',
              eventStatus: event.attendeeStatus,
              eventTypeColor: event.displayOptions?.primaryColor,
              backgroundColor: event.displayOptions?.backgroundColor,
              displayOptions: {
                primaryColor: event.displayOptions?.primaryColor,
                backgroundColor: event.displayOptions?.backgroundColor,
                textColor: event.displayOptions?.textColor,
              },
              // Drag-drop enriched fields
              startHour: dayjs(event.startTime, 'HH:mm:ss').format('hh:mm A'),
              endHour: dayjs(event.endTime, 'HH:mm:ss').format('hh:mm A'),
              startDate: event.startDate,
              endDate: event.endDate,
              locationId: event.locationId,
              locationName: getScopeName(event.locationId ?? ''),
              patientId: event.attendeeId,
              referenceId: event.referenceId,
              appointmentTypeId: event.referenceTypeId,
              appointmentTypeName: event.details?.rTyNa,
            });
            eventIds.push(event.id);
          });
          columns.push({
            id: unassignedColumnId,
            name: 'Unassigned',
            providerImage: '',
            eventIds: eventIds,
            type: 'unassigned',
            count: eventIds.length,
          } as ProviderCalendarColumn);
          columnIds.push(unassignedColumnId);
        }

        columnGroups.push({
          id: locationId,
          name: calendarLocationIds.length > 1 ? getScopeName(locationId) : '',
          columnIds: applyColumnOrder(columnIds, columnEntityOrder?.provider?.[locationId]),
          groupOffset,
          timezone: locationTimezoneMap[locationId]?.timezone || getLocalTimezone(),
        });

        groupOffset += columnIds.length;
      }

      return { headerType, columns, events, columnGroups } as CalendarGridV3DataByType<'provider'>;
    }
  }, [
    headerType,
    calendarLocationIds,
    isOperatoryView,
    getScopeName,
    locationTimezoneMap,
    sortedOperatories,
    calendarDate,
    calendarEventsWithConvertedTimes,
    scheduledRequestsWithConvertedTimes,
    providerSchedules,
    sortedProviders,
    columnEntityOrder,
  ]);
};

type GridEventTiming = {
  startTime: string; // 'HH:mm', clipped to the visible calendarDate's grid rows
  duration: number; // minutes, clipped to the visible day
  isYesterdayEvent: boolean;
  overlappingIndicatorVariant?: OverlappingEventIndicatorVariantType;
};

/**
 * Clips an event's absolute start/end (which may span midnight) to the single day the grid renders,
 * mirroring getEventPlacementValues in calendar-view-v3/helper.ts but for the grid's minute/row model.
 * Sets isYesterdayEvent/overlappingIndicatorVariant so GridAppointmentCard/GridScheduleRequestCard can
 * render the existing "continued"/"+1 day" indicator.
 */
function getGridEventTiming(
  calendarDate: string, // 'MM/DD/YYYY'
  startDate: string | undefined, // 'YYYY-MM-DD'
  startTime: string | undefined, // 'HH:mm:ss'
  endDate: string | undefined, // 'YYYY-MM-DD'
  endTime: string | undefined // 'HH:mm:ss'
): GridEventTiming {
  const dayStart = dayjs(calendarDate, 'MM/DD/YYYY');
  const eventStart = dayjs(`${startDate} ${startTime}`);
  const eventEnd = dayjs(`${endDate} ${endTime}`);

  const fallbackStartTime = startTime ? dayjs(startTime, 'HH:mm:ss').format('HH:mm') : '00:00';
  if (!dayStart.isValid() || !eventStart.isValid() || !eventEnd.isValid()) {
    return { startTime: fallbackStartTime, duration: 0, isYesterdayEvent: false };
  }

  const isYesterdayEvent = eventStart.isBefore(dayStart, 'day');
  const isOverlappingEvent = eventEnd.isAfter(dayStart.endOf('day'));

  const totalDurationMinutes = Math.max(eventEnd.diff(eventStart, 'minute'), 0);
  const clippedStartMinutes = isYesterdayEvent ? 0 : eventStart.diff(dayStart.startOf('day'), 'minute');
  const clippedEndMinutes = isOverlappingEvent ? 24 * 60 : eventEnd.diff(dayStart.startOf('day'), 'minute');

  return {
    startTime: dayjs().startOf('day').add(clippedStartMinutes, 'minute').format('HH:mm'),
    duration: Math.max(clippedEndMinutes - clippedStartMinutes, 0),
    isYesterdayEvent,
    overlappingIndicatorVariant:
      isYesterdayEvent || isOverlappingEvent ? getOverlappingEventIndicatorVariant(totalDurationMinutes) : undefined,
  };
}

/**
 * Applies a saved column order map (columnId → orderIndex) to a list of column IDs.
 * Columns present in the order map are sorted by their saved index; any columns not in
 * the map (e.g. newly added providers) are appended at the end in their original order.
 * Unassigned columns (id starts with "unassigned-") are always kept at the very end,
 * matching the behaviour of moveUnassignedCalendarColumnEntity in the timeline view.
 *
 * @param columnIds - the full list of column IDs built from the data hook
 * @param orderMap  - saved orderIdMap from ScheduleCalendarViewColumnOrder preference
 */
function applyColumnOrder(columnIds: string[], orderMap: Record<string, number> | undefined): string[] {
  if (!orderMap || Object.keys(orderMap).length === 0) return columnIds;

  const unassigned = columnIds.filter((id) => id.startsWith('unassigned-'));
  const ordered = columnIds.filter((id) => !id.startsWith('unassigned-'));

  ordered.sort((a, b) => {
    const orderA = orderMap[a] ?? Infinity;
    const orderB = orderMap[b] ?? Infinity;
    return orderA - orderB;
  });

  return [...ordered, ...unassigned];
}

const getName = (calendarEvent: SchemaCalendarEvent) => {
  return (calendarEvent.attendeeDetails && getFullName(calendarEvent.attendeeDetails)) || 'Unknown';
};
